import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { LockSimple, LockSimpleOpen, ArrowsDownUp } from '@phosphor-icons/react'
import { isApiError } from '@/api'
import { Banner } from '@/ui'
import { errorDetail, errorMessage } from '../api/core'
import {
  mapClearBlockers,
  useClearHold,
  useHoldLot,
  useMoveToOrdinary,
  type ClearBlockers,
} from '../api/gate'
import { HOLD_REASONS } from '../labels'
import { ActionButton } from './Can'
import { ConfirmDialog } from './ConfirmDialog'
import { useScope } from '../api/appConfig'

export interface HoldTarget {
  id: string
  code: string
  hold: string
  stream: string
  holdReasons: string[]
  /** From the lot view (GET /units/:id → holds.clear_blockers) when known. */
  clearBlockers?: ClearBlockers | null
  status?: string
}

/** What stops a clear, in words, with links to the exceptions. */
export function ClearBlockersNote({
  blockers,
  unitId,
}: {
  blockers: ClearBlockers
  unitId?: string
}) {
  const items: ReactNode[] = []
  if (blockers.openExceptionIds.length)
    items.push(
      <>
        {blockers.openExceptionIds.length} open exception
        {blockers.openExceptionIds.length > 1 ? 's' : ''} —{' '}
        <Link
          className="underline"
          to={`/c/exceptions?status=OPEN${unitId ? `&unitId=${unitId}` : ''}`}
        >
          resolve or waive them first
        </Link>
      </>,
    )
  if (blockers.labReportNeeded)
    items.push(
      <>
        A verified passing lab report on the sealed sample is needed
        {blockers.labReasons.length ? ` (${blockers.labReasons.join(', ')})` : ''} — or move the lot
        to ordinary
      </>,
    )
  for (const o of blockers.otherBlockers) items.push(<>{o}</>)
  if (!items.length) return null
  return (
    <Banner tone="warn" title="What blocks a clear">
      <ul className="list-disc pl-4">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </Banner>
  )
}

/**
 * Put on hold / Clear hold / Move to ordinary for a lot (F4.10-j, reused on Stock on hand F5.6-l).
 * A refused clear (409) shows what blocks it: open exception ids and whether a lab report is needed.
 */
export function HoldActions({
  lot,
  size = 'sm',
  compact,
}: {
  lot: HoldTarget
  size?: 'sm' | 'md'
  compact?: boolean
}) {
  const { v1 } = useScope()
  const [dialog, setDialog] = useState<'hold' | 'clear' | 'ordinary' | null>(null)
  const [refused, setRefused] = useState<{
    message: string
    blockers: ClearBlockers | null
  } | null>(null)
  const hold = useHoldLot()
  const clear = useClearHold((e) => {
    if (isApiError(e) && e.kind === 'conflict') {
      const detail = errorDetail(e)
      // uc-core's error envelope may drop the extra fields; fall back to what the lot view knew.
      const fromBody =
        'open_exception_ids' in detail || 'lab_report_needed' in detail
          ? mapClearBlockers(detail)
          : null
      setRefused({ message: errorMessage(e), blockers: fromBody ?? lot.clearBlockers ?? null })
      return true
    }
  })
  const ordinary = useMoveToOrdinary()
  const onHold = lot.hold !== 'NONE'
  const quarantine = lot.hold === 'QUARANTINE'
  const isVoid = lot.status === 'VOID'
  const close = () => {
    setDialog(null)
    setRefused(null)
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {!onHold && !isVoid && (
          <ActionButton
            perm="units:hold"
            hide={compact}
            size={size}
            variant="secondary"
            leadingIcon={<LockSimple />}
            onClick={() => setDialog('hold')}
          >
            Put on hold
          </ActionButton>
        )}
        {onHold && (
          <ActionButton
            perm="units:clear-hold"
            hide={compact}
            size={size}
            variant="secondary"
            leadingIcon={<LockSimpleOpen />}
            blockedReason={
              !quarantine ? `A ${lot.hold} hold is lifted only by the release process` : null
            }
            onClick={() => setDialog('clear')}
          >
            Clear hold
          </ActionButton>
        )}
        {lot.stream === 'MAIN' && !isVoid && (
          <ActionButton
            perm="units:move-to-ordinary"
            hide={compact}
            size={size}
            variant="ghost"
            leadingIcon={<ArrowsDownUp />}
            onClick={() => setDialog('ordinary')}
          >
            Move to ordinary
          </ActionButton>
        )}
      </div>

      <ConfirmDialog
        open={dialog === 'hold'}
        onClose={close}
        title={`Put ${lot.code} on hold`}
        description={
          v1
            ? 'The lot and its bags stay where they are and stay out of the brand until the hold is cleared. A quality hold does not stop his payment.'
            : 'The lot and its bags stay where they are; payment waits until the hold is cleared.'
        }
        confirmLabel="Put on hold"
        tone="danger"
        loading={hold.isPending}
        reason={{
          label: 'Reason',
          choices: HOLD_REASONS,
          allowOther: true,
          asCode: true,
          pattern: /^[A-Z0-9_]{2,40}$/,
          patternMessage: '2–40 letters, digits or _',
        }}
        onConfirm={(reason) => hold.mutateAsync({ id: lot.id, reason }).then(close)}
      />

      <ConfirmDialog
        open={dialog === 'clear'}
        onClose={close}
        title={`Clear the hold on ${lot.code}`}
        description={
          lot.holdReasons.length ? `On hold for: ${lot.holdReasons.join(', ')}` : undefined
        }
        confirmLabel="Clear hold"
        loading={clear.isPending}
        reason={{ label: 'Note', required: false, placeholder: 'What was checked' }}
        onConfirm={(note) => clear.mutateAsync({ id: lot.id, note }).then(close)}
      >
        {refused ? (
          <>
            <Banner tone="error" title="Hold not cleared">
              {refused.message}
            </Banner>
            {refused.blockers && <ClearBlockersNote blockers={refused.blockers} unitId={lot.id} />}
          </>
        ) : (
          lot.clearBlockers && <ClearBlockersNote blockers={lot.clearBlockers} unitId={lot.id} />
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'ordinary'}
        onClose={close}
        title={`Move ${lot.code} to ordinary`}
        description="The lot leaves the good (brand) stream for good — it can never move back. Its bags need a red tie, and any needs-a-test hold is lifted."
        confirmLabel="Move to ordinary"
        tone="danger"
        loading={ordinary.isPending}
        reason={{
          label: 'Reason',
          minLength: 4,
          placeholder: 'Why this lot cannot stay in the good stream',
        }}
        onConfirm={(reason) => ordinary.mutateAsync({ id: lot.id, reason }).then(close)}
      />
    </>
  )
}
